import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAcademy } from '../context/AcademyContext';
import SafeIcon from '../common/SafeIcon';
import { api } from '../api/nocode';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import RadioGroup from '../components/ui/RadioGroup';
import { getCampStatus, computeTrailStartDate } from '../utils/campLogic';

export default function CamperLayout() {
  const { user, logout, setUser } = useAuth();
  const { currentEnrollment, currentRun } = useAcademy();
  const location = useLocation();
  const currentSearch = location.search;
  const runSlug = location.pathname.split('/')[2] || '';
  const runBase = `/hub/${runSlug}`;
  const scopedPath = (path) => {
    if (path.startsWith('/dashboard')) {
      return `${runBase}${path.slice('/dashboard'.length)}`;
    }
    return `${runBase}${path}`;
  };

  const safeUser = Array.isArray(user) ? user[0] : user;
  const runUser = currentEnrollment?.pass_type
    ? { ...safeUser, pass_type: currentEnrollment.pass_type }
    : safeUser;
  const { isAAP } = getCampStatus(runUser, {});
  
  const [toolsEnabled, setToolsEnabled] = useState(false);
  const [onboardingForm, setOnboardingForm] = useState({
    genre: safeUser?.genre || '',
    journey_stage: safeUser?.journey_stage || '',
    wants_bunkmate: safeUser?.wants_bunkmate === 1 || safeUser?.wants_bunkmate === true ? 'yes' : safeUser?.wants_bunkmate === 0 || safeUser?.wants_bunkmate === false ? 'no' : '',
    gender: safeUser?.gender || ''
  });
  const [savingOnboarding, setSavingOnboarding] = useState(false);
  const [onboardingError, setOnboardingError] = useState('');

  useEffect(() => {
    const checkToolsEnabled = async () => {
      try {
        const res = await api.get('settings_gwaacademy');
        const data = Array.isArray(res) ? res : res?.data || [];
        const toolsSetting = data.find(s => s.setting_key === 'show_tools_section' || s.setting_key === 'tools_enabled');
        if (toolsSetting) {
          setToolsEnabled(toolsSetting.value === 'true' || toolsSetting.value === '1');
        }
      } catch (err) {
        console.warn('Could not check tools setting in sidebar', err);
      }
    };

    checkToolsEnabled();
  }, [currentRun?.id]);

  const navItems = [
    { path: `/dashboard${currentSearch}`, matchPath: '/dashboard', label: 'Dashboard', icon: 'Home' },
    { path: `/lesson${currentSearch}`, matchPath: '/lesson', label: 'Morning Lesson', icon: 'Edit' },
    { path: `/lab${currentSearch}`, matchPath: '/lab', label: 'Midday Lab', icon: 'Monitor' },
    { path: `/studyhall${currentSearch}`, matchPath: '/studyhall', label: 'Study Hall', icon: 'BookOpen' },
    { path: `/commons${currentSearch}`, matchPath: '/commons', label: 'The Commons', icon: 'Users' },
  ];

  if (toolsEnabled) {
    navItems.push({ path: `/tools${currentSearch}`, matchPath: '/tools', label: 'Tools', icon: 'Tool' });
  }

  if (isAAP) {
    navItems.push({ path: `/letters${currentSearch}`, matchPath: '/letters', label: 'Self-Letter Studio', icon: 'Mail' });
    navItems.push({ path: `/bunkmate${currentSearch}`, matchPath: '/bunkmate', label: 'Lab Partner', icon: 'UserPlus' });
    navItems.push({ path: `/podcast${currentSearch}`, matchPath: '/podcast', label: 'Private Podcast', icon: 'Headphones' });
  }

  if (safeUser && safeUser.role !== 'admin' && !safeUser.first_login_complete) {
    const isFormValid = onboardingForm.genre && onboardingForm.journey_stage && onboardingForm.wants_bunkmate && (onboardingForm.wants_bunkmate === 'no' || onboardingForm.gender);

    const handleOnboardingSubmit = async () => {
      setSavingOnboarding(true);
      setOnboardingError('');
      try {
        const payloadToSave = {
          genre: onboardingForm.genre,
          journey_stage: onboardingForm.journey_stage,
          wants_bunkmate: onboardingForm.wants_bunkmate === 'yes',
          gender: onboardingForm.wants_bunkmate === 'yes' ? onboardingForm.gender : '',
          first_login_complete: true
        };

        if (['evergreen', 'evergreen-aap'].includes(safeUser.pass_type) && !safeUser.trail_start_date) {
           payloadToSave.trail_start_date = computeTrailStartDate();
        }
        
        if (safeUser.id) {
          await api.put('students_gwaacademy', safeUser.id, payloadToSave);
        }
        
        const journeyStageMap = {
          'idea': 'Idea',
          'draft': 'Draft in Progress',
          'rough': 'Rough Complete',
          'ready': 'Ready to Publish',
          'published': 'Already Published'
        };
        const genreMap = {
          'fiction': 'Fiction',
          'nonfiction': 'Nonfiction'
        };
        
        const payload = {
          name: safeUser.name || 'Author',
          email: safeUser.email || '',
          genre: genreMap[onboardingForm.genre] || onboardingForm.genre,
          journey_stage: journeyStageMap[onboardingForm.journey_stage] || onboardingForm.journey_stage,
          wants_bunkmate: onboardingForm.wants_bunkmate === 'yes' ? 1 : 0,
          gender: onboardingForm.wants_bunkmate === 'yes' ? onboardingForm.gender : ''
        };

        fetch('https://connect.pabbly.com/webhook-listener/webhook/IjU3NjEwNTZhMDYzZTA0M2M1MjZlNTUzNCI_3D_pc/IjU3NjcwNTZlMDYzZTA0MzY1MjY5NTUzMzUxM2Ei_pc', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(webhookErr => {
          console.error("Failed to send data to Pabbly webhook (non-fatal)", webhookErr);
        });

        setUser({ ...safeUser, ...payloadToSave });
      } catch (err) {
        console.error("Onboarding failed", err);
        setOnboardingError(err.message || 'Failed to save onboarding data. Please try again.');
      } finally {
        setSavingOnboarding(false);
      }
    };

    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4 py-12">
        <Card className="max-w-2xl w-full border-2 border-forest-green shadow-xl p-8 md:p-10">
          <div className="text-center space-y-3 mb-8">
            <div className="mx-auto w-16 h-16 bg-forest-green rounded-full flex items-center justify-center text-white mb-4 shadow-md">
              <SafeIcon name="Compass" className="text-3xl" />
            </div>
            <h1 className="text-3xl font-bold text-dark-green font-poppins">One quick thing before you head to academy.</h1>
            <p className="text-muted text-lg">Help us tailor your experience by answering a few quick questions.</p>
          </div>
          
          {onboardingError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md text-sm text-center font-medium">
              {onboardingError}
            </div>
          )}

          <div className="space-y-8 bg-gray-50/50 p-6 rounded-md border border-tan mb-8">
            <RadioGroup 
              label="What do you write?" 
              name="genre"
              value={onboardingForm.genre} 
              onChange={(val) => setOnboardingForm({...onboardingForm, genre: val})}
              options={[
                { value: 'fiction', label: 'Fiction' },
                { value: 'nonfiction', label: 'Nonfiction' }
              ]}
            />
            
            <RadioGroup 
              label="Where are you in your journey?" 
              name="journey_stage"
              value={onboardingForm.journey_stage} 
              onChange={(val) => setOnboardingForm({...onboardingForm, journey_stage: val})}
              options={[
                { value: 'idea', label: 'Idea' },
                { value: 'draft', label: 'Draft in Progress' },
                { value: 'rough', label: 'Rough Complete' },
                { value: 'ready', label: 'Ready to Publish' },
                { value: 'published', label: 'Already Published' }
              ]}
            />

            <div className="border-t border-tan pt-8 mt-4">
              <RadioGroup 
                label="Would you like to be paired with a Lab Partner?" 
                name="wants_bunkmate"
                value={onboardingForm.wants_bunkmate} 
                onChange={(val) => setOnboardingForm({...onboardingForm, wants_bunkmate: val})}
                options={[
                  { value: 'yes', label: 'Yes, pair me up!', description: 'A bonus perk for All-Access Pass holders.' },
                  { value: 'no', label: 'No thanks, going solo.' }
                ]}
              />

              {onboardingForm.wants_bunkmate === 'yes' && (
                <div className="mt-6 p-5 bg-white border-2 border-forest-green rounded-md shadow-sm transition-all duration-300">
                  <RadioGroup 
                    label="To help us find the best match, what is your gender?" 
                    name="gender"
                    value={onboardingForm.gender} 
                    onChange={(val) => setOnboardingForm({...onboardingForm, gender: val})}
                    options={[
                      { value: 'male', label: 'Male' },
                      { value: 'female', label: 'Female' }
                    ]}
                  />
                </div>
              )}
            </div>
          </div>

          <Button 
            className="w-full text-lg py-4 shadow-md"
            disabled={!isFormValid || savingOnboarding}
            onClick={handleOnboardingSubmit}
          >
            {savingOnboarding ? 'Preparing Your Academy...' : 'Head to Academy →'}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-dark-green text-white flex flex-col no-print">
        <div className="p-6">
          <Link to={scopedPath(`/dashboard${currentSearch}`)} className="block hover:opacity-90 transition-opacity">
            <h1 className="text-2xl font-bold text-amber">GWA Academy</h1>
            <p className="text-sm text-tan opacity-80">GospelWise Author Academy</p>
          </Link>
        </div>
        
        <nav className="flex-1 px-4 space-y-2">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={scopedPath(item.path)}
              className={`flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                location.pathname === scopedPath(item.matchPath) ? 'bg-forest-green text-white' : 'text-tan hover:bg-forest-green/50'
              }`}
            >
              <SafeIcon name={item.icon} className="text-lg" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        
        <div className="p-4 border-t border-forest-green">
          <Link to={scopedPath(`/profile${currentSearch}`)} className="flex items-center gap-3 px-4 py-2 text-tan hover:text-white">
            <SafeIcon name="User" /> Profile
          </Link>
          {safeUser?.role === 'admin' && (
            <Link to="/admin" className="flex items-center gap-3 px-4 py-2 text-amber hover:text-orange-400 mt-2">
              <SafeIcon name="Settings" /> Admin Panel
            </Link>
          )}
          <button onClick={logout} className="flex items-center gap-3 px-4 py-2 text-tan hover:text-white w-full text-left mt-2">
            <SafeIcon name="LogOut" /> Logout
          </button>
        </div>
      </aside>
      
      <main className="flex-1 flex flex-col min-h-screen md:max-h-screen md:overflow-y-auto bg-cream print-container">
        {!isAAP && (
          <div className="bg-amber text-white px-6 py-3 flex justify-between items-center sticky top-0 z-10 no-print">
            <span className="font-medium">Get the most out of academy with the All-Access Pass!</span>
            <Link to={scopedPath(`/upgrade${currentSearch}`)} className="bg-white text-amber px-4 py-1 rounded-md text-sm font-bold hover:bg-tan transition-colors">
              Upgrade Now
            </Link>
          </div>
        )}
        <div className="p-6 md:p-10 flex-1 print-container">
          <Outlet />
        </div>
      </main>
    </div>
  );
}